import React from 'react';
import { useIsFetching } from '@tanstack/react-query';

/**
 * A thin bar across the top whenever ANY query is in flight.
 *
 * Most of the app's loading is invisible: React Query refetches on window focus
 * and the dashboard refreshes itself every 30 seconds, and during those the
 * page shows stale numbers with nothing to say so. `useIsFetching` counts every
 * in-flight query in the app, so one component covers the lot — no page needs
 * to know about it.
 *
 * Deliberately a bar and not an overlay: a background refresh must never block
 * what someone is already reading or typing.
 */
export function GlobalLoadingBar() {
  const fetching = useIsFetching();
  if (!fetching) return null;
  return (
    <div className="fixed top-0 left-0 right-0 z-[60] h-0.5 bg-orange-100 overflow-hidden"
      role="status" aria-live="polite">
      <div className="h-full w-1/4 bg-orange-500 animate-loading-sweep" />
      <span className="sr-only">Loading…</span>
    </div>
  );
}

/** Inline spinner + message, for a panel that has nothing to show yet. */
export default function Loading({ label = 'Loading…', className = '' }) {
  return (
    <div className={`flex items-center justify-center gap-2 py-8 text-gray-400 ${className}`}>
      <span className="w-4 h-4 border-2 border-gray-200 border-t-orange-500 rounded-full animate-spin" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

/**
 * Grey blocks in the shape of the content that is coming.
 *
 * Used where a number would otherwise render as a real-looking zero before its
 * data arrives — an empty dashboard tile reads as "R0.00 today", which is worse
 * than admitting it has not loaded.
 */
export function Skeleton({ className = '' }) {
  return <div className={`bg-gray-200 rounded animate-pulse ${className}`} />;
}
